import React, { useCallback, useEffect, useState } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import clsx from 'clsx';
import _get from 'lodash/get';
import _isEqual from 'lodash/isEqual';

import { formatCurrency } from '@atc/string-fns';

import { Collapsible, ColoredBackground, Glyphicon, Image, LazyRender, LoadingSpinner } from 'reaxl';

import { priceAdvisorText } from 'axl-config';

import {
    globalConfigsDuck,
    kbbVRSDataDuck,
    queryDuck,
} from '@/ducks';

import {
    bapKbbFairMarketRangeDuck,
    bapSelectedOptionsDuck,
} from '@/ducks/bap';

import PriceAdvisor from '@/components/PriceAdvisor';

const usdCurrency = (currency) => formatCurrency(currency || 0, 'USD');

const BuildAndPriceFairMarketRangeContainer = React.memo(() => {
    const dispatch = useDispatch();

    const kbbFairMarketRange = useSelector(bapKbbFairMarketRangeDuck.selectors.getKbbFairMarketRange);
    const selectedOptions = useSelector(bapSelectedOptionsDuck.selectors.getDuckState);
    const trimInfo = useSelector(kbbVRSDataDuck.selectors.getSelectedTrimInfo);
    const query = useSelector(queryDuck.selectors.getDuckState);

    const zip = query?.zip;
    const [showPriceAdvisor, setShowPriceAdvisor] = useState(false);
    const [isWidgetLoading, setIsWidgetLoading] = useState(true);

    const priceAdvisorApiUrl = useSelector(globalConfigsDuck.selectors.getPriceAdvisorApiUrl);
    const priceAdvisorApiKey = useSelector(globalConfigsDuck.selectors.getPriceAdvisorApiKey);

    const onToggle = useCallback(() => setShowPriceAdvisor((prevState) => !prevState), []);

    const onLoadWidgetHandle = useCallback(() => setIsWidgetLoading(false), []);

    let { vehicleId } = trimInfo;

    const selectedDriveTrain = selectedOptions?.availableDriveTrainList?.optionName;
    if (selectedDriveTrain && trimInfo?.driveTypeInfo?.[selectedDriveTrain]) {
        vehicleId = trimInfo.driveTypeInfo[selectedDriveTrain]?.vehicleId || vehicleId;
    }

    useEffect(() => {
        dispatch(bapKbbFairMarketRangeDuck.creators.loadFairMarketRanges({ zip, vehicleId, selectedOptions }));
    }, [dispatch, zip, vehicleId, selectedOptions]);

    const { rangeLow, rangeHigh } = kbbFairMarketRange || {};

    const hasKbbFairMarketRange = !!(rangeLow && rangeHigh);

    const selectedOptionIds = Object.values(selectedOptions || {}).map((opt) => opt?.vehicleOptionId).filter(Boolean);

    return (
        <LazyRender>
            <ColoredBackground
                uiContext="inverted"
                className={clsx('padding-top-3', { 'padding-bottom-3': !hasKbbFairMarketRange })}
            >
                <div
                    data-cmp="cntnr-prf-fairMarketRange"
                    className="display-grid"
                    style={{ justifyItems: 'center' }}
                >
                    <div className="margin-bottom-2">
                        <Image
                            className="margin-right-1"
                            src="https://www.autotrader.com/content/dam/autotrader/additionalresources/kbb_logo.svg"
                            alt="Price Advisor"
                            lazyload={false}
                            width="29"
                            height="40"
                        />
                        Kelley Blue Book® Fair Market Range
                    </div>
                    {hasKbbFairMarketRange ? (
                        <div
                            className="margin-bottom-3 text-size-700"
                            style={{ lineHeight: 'normal' }}
                        >
                            {usdCurrency(rangeLow)}
                            {' '}
                            -
                            {' '}
                            {usdCurrency(rangeHigh)}
                        </div>
                    ) : (
                        <p
                            className="text-left"
                            style={{ width: 260 }}
                        >
                            {_get(priceAdvisorText, 'priceAdvisorSubHeader.unavailable')}
                        </p>
                    )}
                </div>
                {hasKbbFairMarketRange && (
                    <Collapsible
                        key="displayName"
                        toggleHeader={(
                            <div className="bg-primary text-center text-size-200 padding-bottom-3 cursor-pointer">
                                See More Details
                                <Glyphicon
                                    className="margin-left-1"
                                    glyph={showPriceAdvisor ? 'chevron-up' : 'chevron-down'}
                                />
                            </div>
                        )}
                        initialCollapse
                        onToggle={onToggle}
                    >
                        <div
                            className="padding-vertical-5 bg-neutral-primary text-gray-base"
                            style={{ borderBottom: '1px solid #e1e4e8' }}
                        >
                            <div className="text-center margin-bottom-5">Note: This price range does not include package selections.</div>
                            {isWidgetLoading && (<LoadingSpinner className="margin-top-0" />)}
                            <PriceAdvisor
                                displayFPP
                                showAskingPrice
                                listingType="new"
                                vin=""
                                apiUrl={priceAdvisorApiUrl}
                                clientId={priceAdvisorApiKey}
                                zip={zip}
                                vehicleId={vehicleId}
                                optionIds={selectedOptionIds.join(',')}
                                onLoad={onLoadWidgetHandle}
                                height={isWidgetLoading ? '0' : '280'}
                            />
                        </div>
                    </Collapsible>
                )}
            </ColoredBackground>
        </LazyRender>
    );
}, _isEqual);

export default BuildAndPriceFairMarketRangeContainer;
